import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box,
  Typography,
  Divider
} from '@mui/material';
import {
  LocalOffer as LocalOfferIcon
} from '@mui/icons-material';
import FeaturedProducts from '../../components/store/FeaturedProducts';
import BestSellerProducts from '../../components/store/BestSellerProducts';
import ProductList from '../../components/store/ProductList';
import LatestProducts from '../../components/store/LatestProducts';
import { StoreBanner, ProductPromotion } from '../../components/store';
import { Promotion } from '../../components/store/ProductPromotion';
import { Banner } from '../../components/store/StoreBanner';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';
import { useScrollToTop } from '../../hooks/useScrollToTop';

interface StorePageContext {
  searchQuery?: string;
  selectedCategoryId?: string;
}

const StorePage: React.FC = () => {
  const { searchQuery = '', selectedCategoryId } = useOutletContext<StorePageContext>();
  // استخدام إعدادات المتجر
  const { settings, loading } = useStoreSettings();

  // التمرير إلى أعلى الصفحة عند تغيير الفئة أو البحث
  useScrollToTop([selectedCategoryId, searchQuery]);

  // بيانات البانر من إعدادات المتجر
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loadingBanners, setLoadingBanners] = useState(true);
  const bannersInitialized = useRef(false);

  // بيانات الإعلانات الترويجية من إعدادات المتجر
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const defaultBanners: Banner[] = [
    {
      id: '1',
      title: 'عروض حصرية على الأجهزة الإلكترونية',
      subtitle: 'خصومات تصل إلى 30% على جميع المنتجات',
      imageUrl: 'https://source.unsplash.com/random/1200x400/?electronics',
      buttonText: 'تسوق الآن',
      buttonLink: '/store/categories',
      backgroundColor: '#1976d2',
      textColor: '#ffffff'
    },
    {
      id: '2',
      title: 'أحدث الهواتف الذكية',
      subtitle: 'اكتشف أحدث التقنيات بأفضل الأسعار',
      imageUrl: 'https://source.unsplash.com/random/1200x400/?smartphone',
      buttonText: 'اكتشف المزيد',
      buttonLink: '/product/smartphone',
      backgroundColor: '#673ab7',
      textColor: '#ffffff'
    },
    {
      id: '3',
      title: 'خدمات الصيانة المتميزة',
      subtitle: 'صيانة احترافية لجميع أنواع الأجهزة',
      imageUrl: 'https://source.unsplash.com/random/1200x400/?repair',
      buttonText: 'طلب صيانة',
      buttonLink: '/maintenance/create',
      backgroundColor: '#2196f3',
      textColor: '#ffffff'
    }
  ];

  const defaultPromotions: Promotion[] = [
    {
      id: '1',
      title: 'أجهزة منزلية بخصم 20%',
      description: 'تسوق الآن واحصل على خصم حصري على جميع الأجهزة المنزلية',
      imageUrl: 'https://source.unsplash.com/random/600x300/?appliances',
      backgroundColor: '#f44336',
      textColor: '#ffffff',
      buttonText: 'تسوق الآن',
      buttonLink: '/store/categories',
      position: 'right',
      size: 'medium'
    },
    {
      id: '2',
      title: 'أحدث الهواتف الذكية',
      description: 'اكتشف مجموعتنا الجديدة من الهواتف الذكية بأفضل الأسعار',
      imageUrl: 'https://source.unsplash.com/random/600x300/?smartphone',
      backgroundColor: '#2196f3',
      textColor: '#ffffff',
      buttonText: 'اكتشف المزيد',
      buttonLink: '/product/smartphone',
      position: 'left',
      size: 'medium'
    }
  ];

  // تهيئة البانرات الافتراضية مرة واحدة عند تحميل الصفحة لتجنب الوميض
  useEffect(() => {
    if (!bannersInitialized.current) {
      setBanners(defaultBanners);
      setPromotions(defaultPromotions);
      bannersInitialized.current = true;
    }
  }, []);

  // تحديث البانرات والإعلانات من إعدادات المتجر
  useEffect(() => {
    if (loading) {
      setLoadingBanners(true);
      return;
    }

    if (settings && settings.display) {
      // تحديث البانرات من الإعدادات فقط إذا كانت موجودة
      if (settings.display.banners && settings.display.banners.length > 0) {
        setBanners(settings.display.banners);
      }

      // تحديث الإعلانات من الإعدادات فقط إذا كانت موجودة
      if (settings.display.promotions && settings.display.promotions.length > 0) {
        setPromotions(settings.display.promotions);
      }
    }

    setLoadingBanners(false);
  }, [settings, loading]);

  const sectionsOrder = settings?.display?.homepageSections || [
    { id: 'banners', label: 'البانرات الترويجية', enabled: settings?.display?.showBanners !== false },
    { id: 'promotions', label: 'العروض الخاصة', enabled: settings?.display?.showPromotions !== false },
    { id: 'featured', label: 'المنتجات المميزة', enabled: true },
    { id: 'bestsellers', label: 'الأكثر مبيعاً', enabled: true },
    { id: 'latest', label: 'أحدث المنتجات', enabled: true }
  ];

  const renderSection = (id: string) => {
    switch (id) {
      case 'banners':
        return (
          <StoreBanner key="banners" banners={banners} loading={loadingBanners} />
        );
      case 'promotions':
        return (
          <ProductPromotion key="promotions" promotions={promotions} loading={loadingBanners} />
        );
      case 'featured':
        return (
          <Box id="featured-products-section" key="featured" sx={{ mb: 6 }}>
            <FeaturedProducts maxItems={4} />
          </Box>
        );
      case 'bestsellers':
        return (
          <Box id="best-seller-products-section" key="bestsellers" sx={{ mb: 6 }}>
            <BestSellerProducts maxItems={4} />
          </Box>
        );
      case 'latest':
        return (
          <Box id="latest-products-section" key="latest" sx={{ mb: 6 }}>
            <LatestProducts title="أحدث المنتجات" maxItems={8} />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Box sx={{ mb: 8 }}>
        {sectionsOrder
          .filter(section => section.enabled)
          .map(section => renderSection(section.id))}

        {/* قائمة المنتجات حسب الفئة أو البحث */}
        {(selectedCategoryId || searchQuery) && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 6, mb: 3 }}>
              <LocalOfferIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h5" component="h2" fontWeight="bold">
                {searchQuery ? `نتائج البحث: ${searchQuery}` : 'منتجات الفئة'}
              </Typography>
              <Divider sx={{ flexGrow: 1, ml: 2 }} />
            </Box>
            <ProductList
              categoryId={selectedCategoryId}
              searchQuery={searchQuery}
            />
          </>
        )}
      </Box>
    </>
  );
};

export default StorePage;
